import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import type { Site } from '../../api/client';

interface TrackingCodeProps {
  site: Site;
}

export default function TrackingCode({ site }: TrackingCodeProps) {
  const [copied, setCopied] = useState(false);

  const snippet = `<script>
  (function(v,a,n,t,g,e){v.VantageObject=g;v[g]=v[g]||function(){
  (v[g].q=v[g].q||[]).push(arguments)};e=a.createElement(n);
  e.async=1;e.src=t;a.getElementsByTagName(n)[0].parentNode
  .insertBefore(e,a.getElementsByTagName(n)[0])
  })(window,document,'script','https://cdn.vantage.io/tracker.js','vantage');
  vantage('init', '${site.trackingId}');
  vantage('start');
</script>`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text
    }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tracking Code</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add this snippet to the <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">&lt;head&gt;</code> of your website.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Site ID</p>
          <p className="font-mono text-sm font-medium text-gray-900">{site.trackingId}</p>
        </div>
      </div>

      <div className="relative mt-4">
        <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-300 font-mono leading-relaxed">
          {snippet}
        </pre>
        <button
          onClick={handleCopy}
          className="absolute right-3 top-3 rounded-lg bg-gray-700 p-2 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white"
          title="Copy to clipboard"
        >
          {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      <div className="mt-3 rounded-lg bg-blue-50 p-3">
        <p className="text-xs text-blue-700">
          The tracking snippet will automatically start recording sessions based on your sampling rate.
          Ensure the snippet is placed before the closing <code className="bg-blue-100 px-1 rounded">&lt;/head&gt;</code> tag for best results.
        </p>
      </div>
    </div>
  );
}
